import {queryOptions, useQuery} from '@tanstack/react-query'
import {
	getAdminOrganization,
	getAdminOrganizations,
	getAdminOrgInvitations,
	getAdminOrgUsers,
	getPermitPreview,
	getStaff,
	getStaffInvitations,
} from '@/lib/api/admin'

export const adminOrgsQueryOptions = (includeArchived = false) =>
	queryOptions({
		// The filter joins the key; the prefix invalidation in
		// invalidateAdminOrgQueries still matches both variants, and there is
		// no collision with the detail key (its third element is an orgId
		// string, not an object).
		queryKey: ['admin', 'organizations', {includeArchived}] as const,
		queryFn: async () => (await getAdminOrganizations(includeArchived)).data,
	})

export function useAdminOrgs(includeArchived = false) {
	return useQuery(adminOrgsQueryOptions(includeArchived))
}

export const adminOrgDetailQueryOptions = (orgId: string) =>
	queryOptions({
		queryKey: ['admin', 'organizations', orgId] as const,
		queryFn: async () => (await getAdminOrganization(orgId)).data,
	})

export function useAdminOrgDetail(orgId: string) {
	return useQuery(adminOrgDetailQueryOptions(orgId))
}

export const adminOrgUsersQueryOptions = (orgId: string) =>
	queryOptions({
		queryKey: ['admin', 'organizations', orgId, 'users'] as const,
		queryFn: async () => (await getAdminOrgUsers(orgId)).data,
	})

export function useAdminOrgUsers(orgId: string) {
	return useQuery(adminOrgUsersQueryOptions(orgId))
}

export const adminOrgInvitationsQueryOptions = (orgId: string) =>
	queryOptions({
		queryKey: ['admin', 'organizations', orgId, 'invitations'] as const,
		queryFn: async () => (await getAdminOrgInvitations(orgId)).data,
	})

export function useAdminOrgInvitations(orgId: string) {
	return useQuery(adminOrgInvitationsQueryOptions(orgId))
}

export const staffQueryOptions = () =>
	queryOptions({
		queryKey: ['admin', 'staff'] as const,
		queryFn: async () => (await getStaff()).data,
	})

export function useStaff() {
	return useQuery(staffQueryOptions())
}

export const staffInvitationsQueryOptions = () =>
	queryOptions({
		queryKey: ['admin', 'staff', 'invitations'] as const,
		queryFn: async () => (await getStaffInvitations()).data,
	})

export function useStaffInvitations() {
	return useQuery(staffInvitationsQueryOptions())
}

export const permitPreviewQueryOptions = (from: string) =>
	queryOptions({
		queryKey: ['admin', 'permit-preview', from] as const,
		queryFn: async () => (await getPermitPreview(from)).data,
		staleTime: 60_000,
	})

export function usePermitPreview(from: string, enabled = true) {
	return useQuery({...permitPreviewQueryOptions(from), enabled})
}
